import React from 'react'
import { twJoin } from 'tailwind-merge'
import { Actions } from '~/actions'
import Button from '~/components/Button'
import { Collapsible } from '~/components/Collapsible'
import { Countdown } from '~/components/Countdown'
import { Checkmark, Plus } from '~/components/Icons'
import { useTranslations } from '~/components/Locale'
import { Price } from '~/components/utils'
import type {
	ArrayElement,
	GQLResponseOk,
	HTMLElementProps,
	MockCart,
	Nullable,
	SimpleVariant,
	UpsellQuery
} from '~/types'
import { cn, filesUrl, getPrice, getScriptDiscounts, imageUrl, isMediaImage, UpsellInfo, useDispatch } from '~/utils'

export interface UpsellProps extends Omit<UpsellInfo, 'id'>, HTMLElementProps<'div'> {
	mode: 'product' | 'cart'
	upsellIds?: Array<UpsellInfo['id']>
	variant: ArrayElement<GQLResponseOk<UpsellQuery>['data']>
	mockCart?: Nullable<MockCart>
	/**
	 * Override default add to cart action
	 */
	onAdd?: (v: SimpleVariant & { discounts?: number }) => void
	onRemove?: (v: SimpleVariant) => void
}

export const Upsell: React.FC<UpsellProps> = ({
	className,
	upsellIds,
	expirationText,
	variant,
	label,
	mockCart,
	mode,
	expiration,
	onAdd,
	onRemove,
	...props
}) => {
	const dispatch = useDispatch()
	const t = useTranslations('react')

	const product = variant.product
	const lineItem = mockCart?.lines?.find(li => li.merchandise?.id === variant.id)
	const discounts = lineItem ? getScriptDiscounts(lineItem) : 0
	/* Check length, as empty descriptions still contain some junk HTML */
	const hasInfo = product.descriptionHtml.length > 35 || variant.infoImage
	return (
		<li className="text-right">
			{label && (
				<aside
					className={twJoin(
						'rounded-t-7 mr-5 inline-block px-5',
						'bg-green-75 text-10 font-semibold leading-loose text-green-800'
					)}
				>
					{label}
				</aside>
			)}
			<div
				className={cn(
					'grid grid-cols-[4rem,2fr,min-content,9rem] grid-rows-[auto,auto,auto,min-content] ',
					'items-center space-x-4 bg-gray-100 p-4 text-left',
					mode === 'product' ? 'rounded-7 sm:space-x-6 sm:px-6' : 'rounded-xl',
					className
				)}
				{...props}
			>
				<div
					className="col-start-1 row-span-3 row-start-1 self-center rounded-full bg-cover bg-center"
					style={{
						aspectRatio: '1/1',
						backgroundImage: `url(${imageUrl(product.featuredImage, 100)})`
					}}
				/>
				<span
					className={cn(
						product.subtitle ? 'row-span-1' : 'row-span-3',
						'col-start-2 col-end-3 row-start-1 font-bold',
						mode === 'product' ? 'text-2xl' : 'text-16'
					)}
				>
					{product?.title}
				</span>
				{product.useDescription ? (
					<div
						className="col-span-4 col-start-1 row-start-4 !ml-0 px-10 py-4"
						dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
					/>
				) : (
					<span className={`col-start-2 row-start-2 text-base ${hasInfo ? 'leading-snug' : 'leading-tight'}`}>
						{product.subtitle?.value}
					</span>
				)}
				<span
					className={cn(
						'col-start-4 row-start-1 row-end-2 text-center text-xl font-bold',
						mode === 'product' && 'sm:col-start-3 sm:row-end-4 sm:text-right'
					)}
				>
					{variant.price.amount === '...' ? (
						<span>...</span>
					) : (
						<Price
							className="uppercase"
							original={Math.max(getPrice(variant.price), getPrice(variant.compareAtPrice) + discounts)}
							price={getPrice(variant.price) - discounts}
							asText
						/>
					)}
				</span>

				<div className="col-span-full row-start-3 row-end-3 py-2">{/* Fix grid auto sizing issues*/}</div>
				<div
					className={twJoin(
						'z-[2] col-start-4 row-start-2 row-end-4 !ml-2 flex w-[94px] items-center space-x-2 self-center',
						mode === 'product' && 'sm:row-start-1 md:w-auto',
						upsellIds?.includes(variant.id) ? 'justify-end' : 'justify-center'
					)}
				>
					{upsellIds?.includes(variant.id) ? (
						<>
							<strong className="text-green text-base">{t?.actions.added}</strong>
							<Button size="sm" className="h-8 w-8 px-0 py-0" disabled>
								<Checkmark height="0.8em" className="align-middle" />
							</Button>
						</>
					) : (
						<Button
							size="sm"
							className="flex items-center space-x-1 uppercase"
							onClick={_ =>
								onAdd
									? onAdd({ ...variant, discounts })
									: dispatch(Actions.addToCart([{ quantity: 1, id: variant.id }]))
							}
						>
							<span>{t?.actions.add}</span>
							<Plus />
						</Button>
					)}
					{upsellIds?.includes(variant.id) && onRemove && (
						<Button
							size="sm"
							className="hocus:bg-gray-300 h-8 w-8 bg-transparent px-0 py-0 opacity-70"
							onClick={_ => onRemove(variant)}
						>
							<img className="h-4 align-middle" src={filesUrl('trash.svg')} alt="delete" />
						</Button>
					)}
				</div>
				{expiration && (
					<Countdown
						to={expiration}
						className={cn(
							'col-span-full col-start-1 row-start-5 !ml-0 text-center',
							mode === 'product' ? 'text-2xl' : 'text-xl',
							'text-red-500'
						)}
						separatorClassName={mode === 'product' ? '' : 'mx-px'}
					>
						<span className="text-gray-750">{expirationText ?? t?.presale.offer_ends_in} </span>
					</Countdown>
				)}
				{hasInfo && !product.useDescription && (
					<>
						<Collapsible
							className={
								'col-span-3 col-start-2 row-span-2 row-start-3 !ml-0 w-0 min-w-full ' +
								'text-gray-750 self-baseline text-base'
							}
							summaryClassname="leading-[1.6rem]"
							title={
								<strong className={cn('ml-4', mode === 'product' && 'sm:ml-6')}>
									{t?.products.info}
								</strong>
							}
						>
							<div className="pb-2 pt-4">
								<div dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
								{isMediaImage(variant.infoImage?.reference) && (
									<img src={imageUrl(variant.infoImage?.reference.image, '1024x')} />
								)}
							</div>
						</Collapsible>
						<div className="relative col-start-1 row-start-4 !mx-0 h-full w-[2px] justify-self-center overflow-hidden">
							<div className="absolute top-4 h-full w-full bg-gray-400" />
						</div>
					</>
				)}
			</div>
		</li>
	)
}
